import React from 'react';
import { Outlet, NavLink, useLocation, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChangePasswordModal } from '@/components/ChangePasswordModal';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { Crown, LogOut, MessageSquare, Moon, Plug, Server, Settings, Shield, Sun, Users } from 'lucide-react';

export const PROFILE_MODAL_PARAM = 'profile';
export const PROFILE_MODAL_VALUE = 'change-password';

const navItems = [
  { to: '/', label: 'Заявки на интеграцию', icon: Plug },
  { to: '/client-settings', label: 'Настройки клиента', icon: Settings },
  { to: '/users', label: 'Пользователи и магазины', icon: Users },
  { to: '/subscriptions', label: 'Подписки', icon: Crown },
  { to: '/chats', label: 'Чаты пользователей', icon: MessageSquare },
  { to: '/services', label: 'Состояние сервисов', icon: Server },
  { to: '/admins', label: 'Администраторы', icon: Shield },
];

export function Layout() {
  const { admin, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const showProfileModal = searchParams.get(PROFILE_MODAL_PARAM) === PROFILE_MODAL_VALUE;
  const openProfile = () => {
    setSearchParams(
      (prev) => {
        prev.set(PROFILE_MODAL_PARAM, PROFILE_MODAL_VALUE);
        return prev;
      },
      { replace: false },
    );
  };
  const closeProfile = () => {
    setSearchParams(
      (prev) => {
        prev.delete(PROFILE_MODAL_PARAM);
        return prev;
      },
      { replace: false },
    );
  };

  return (
    <div className="min-h-screen bg-background flex">
      <aside className="w-56 border-r bg-card shrink-0 flex flex-col">
        <div className="p-4 border-b">
          <h1 className="text-lg font-semibold">Админ-панель</h1>
        </div>
        <nav className="flex-1 p-2 space-y-1">
          {navItems.map(({ to, label, icon: Icon }) => {
            const isActive = to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);
            return (
              <NavLink
                key={to}
                to={to}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                {label}
              </NavLink>
            );
          })}
        </nav>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="border-b bg-card shrink-0">
          <div className="px-6 py-4 flex items-center justify-end gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
              className="shrink-0 h-9 w-9 p-0"
            >
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <button
              type="button"
              onClick={openProfile}
              className="text-sm text-muted-foreground hover:text-foreground hover:underline bg-transparent border-0 cursor-pointer p-0"
              data-testid="header-profile-link"
            >
              {admin?.email}
            </button>
            <Button variant="outline" size="sm" onClick={logout}>
              <LogOut className="w-4 h-4 mr-2" />
              Выйти
            </Button>
          </div>
        </header>

        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>

      <ChangePasswordModal open={showProfileModal} onClose={closeProfile} />
    </div>
  );
}
